import { useCallback } from 'react';
import { Masonry } from '@leagueplatform/masonry';
import {
  Button,
  HeadingText,
  ImageMessage,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { MASONRY } from 'common/constants';
import { OnboardingCard } from 'components/onboarding/onboarding-card/onboarding-card.component';
import { useIsOnboardingMobile } from 'hooks/use-is-onboarding-mobile/use-is-onboarding-mobile.hook';
import { useEligibilitySignOut } from 'hooks/use-eligibility-sign-out/use-eligibility-sign-out.hook';
import { signOut } from 'utils/sign-out';
import windingRoadIllustration from 'assets/winding-road-illustration.svg';

// Shown when the root node fetch itself fails, which no node spec can cover. Without
// it the member is left holding a live session on a screen whose only control is the
// one that ends it — the SDK's default fallback offers retry, not a way out.
const IneligibleMemberRootErrorFallback = () => {
  const { formatMessage } = useIntl();

  return (
    <ImageMessage
      image={windingRoadIllustration}
      imageGap="$oneAndHalf"
      imageWidth={176}
    >
      <StackLayout horizontalAlignment="center">
        <HeadingText
          size="lg"
          level="1"
          css={{ textAlign: 'center', marginBlockEnd: '$one' }}
        >
          {formatMessage({ id: 'STR_INELIGIBLE_MEMBER_HEADING' })}
        </HeadingText>
        <ParagraphText css={{ textAlign: 'center' }}>
          {formatMessage({ id: 'STR_INELIGIBLE_MEMBER_BODY' })}
        </ParagraphText>
        <Button
          priority="primary"
          onClick={signOut}
          css={{ marginBlockStart: '$twoAndHalf' }}
        >
          {formatMessage({ id: 'STR_CLOSE' })}
        </Button>
      </StackLayout>
    </ImageMessage>
  );
};

// Server-driven ineligibility screen. The node supplies the illustration, copy and
// the single Close button, which emits a deeplink this view turns into a sign-out.
export const IneligibleMemberView = () => {
  const { formatMessage } = useIntl();
  const isOnboardingMobile = useIsOnboardingMobile();

  const handleSignOut = useCallback(() => {
    signOut();
  }, []);

  useEligibilitySignOut(handleSignOut);
  usePageViewAnalytics({ screen_name: 'Not Eligible' });
  useDocumentTitle(formatMessage({ id: 'STR_INELIGIBLE_MEMBER_HEADING' }));

  return (
    <OnboardingCard
      css={{
        // The shared shell owns the page gutter; keep content inset on mobile.
        padding: isOnboardingMobile ? '$oneAndHalf' : '$twoAndHalf',
        // ImageMessage reserves a gap for children the Masonry renderer never passes.
        '.GDS-image-message > div': { marginBlockEnd: 0 },
      }}
    >
      <Masonry
        rootNodeId={MASONRY.ROOT_NODE_ID_INELIGIBLE_MEMBER}
        rootErrorFallback={IneligibleMemberRootErrorFallback}
      />
    </OnboardingCard>
  );
};
